import { useCallback } from 'react';
import {
  type TranslationFnc, useTranslation,
} from '~/_shared/utils/hooks';
import { memoizeOne } from '~/_shared/utils/memoize/memoize';
import { getCalculateBucketFunctionOptions } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/edit/activeBoundaryFillEditComponent';
import { shortenDemographicsName } from '~/sidebar/sidebarApps/mapTools/boundary/fill/boundaryFill.helpers';
import { useGetBoundaryGroup } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useSpreadsheetColumns } from '~/store/matchupData/matchupDataSelectors.hook';
import { translate } from '~/translations/Trans';

type ActiveBoundaryFillInfoEntitiesBase = Readonly<{
  mainEntity: string;
  subEntity?: string;
}>;

export type ActiveBoundaryFillInfoEntities = ActiveBoundaryFillInfoEntitiesBase & Readonly<{
  shortenedMainEntity: string;
  shortenedSubEntity?: string;
}>;

const shortenEntities = (entities: ActiveBoundaryFillInfoEntitiesBase): ActiveBoundaryFillInfoEntities => {
  const shortenedMain = entities.mainEntity === 'Citizenship Status' ? 'Citizenship' : entities.mainEntity;
  const shortenedSub = entities.subEntity && shortenDemographicsName(entities.subEntity);

  return {
    ...entities,
    shortenedMainEntity: shortenedMain,
    shortenedSubEntity: shortenedSub,
  };
};

const getBoundaryFillEntities = ({
  boundaryTerritoryGroup,
  bucketFunctionOptionName,
  columnName,
  getBoundaryGroupById,
  boundaryTerritoryType,
  t,
}: Readonly<{
  t: TranslationFnc;
  boundaryTerritoryGroup?: BoundaryTerritoryGroup | null;
  bucketFunctionOptionName: string | undefined;
  columnName: string;
  getBoundaryGroupById: ReturnType<typeof useGetBoundaryGroup>['getBoundaryGroupById'];
  boundaryTerritoryType: BoundaryTerritoryType;
}>): ReturnType<typeof shortenEntities> | null => {
  if (!boundaryTerritoryGroup) {
    return null;
  }

  switch (boundaryTerritoryType) {
    case BoundaryTerritoryType.Density: {
      return shortenEntities({ mainEntity: t('Location Density') });
    }

    case BoundaryTerritoryType.Demographic: {
      if (boundaryTerritoryGroup.settings.demographicId) {
        const demographics = getBoundaryGroupById(boundaryTerritoryGroup.boundaryGroupId)?.demographics;
        if (demographics) {
          const selectedDemographic = demographics.find(d => d.id === boundaryTerritoryGroup.settings.demographicId);
          if (selectedDemographic) {
            return shortenEntities({
              mainEntity: selectedDemographic.category,
              subEntity: selectedDemographic.name,
            });
          }
        }
      }
      return null;
    }

    case BoundaryTerritoryType.Groups: {
      return {
        mainEntity: t('Group'),
        shortenedMainEntity: t('Group'),
        subEntity: columnName,
        shortenedSubEntity: columnName,
      };
    }

    case BoundaryTerritoryType.Numeric: {
      if (bucketFunctionOptionName) {
        return {
          mainEntity: bucketFunctionOptionName,
          shortenedMainEntity: bucketFunctionOptionName,
          subEntity: columnName,
          shortenedSubEntity: columnName,
        };
      }
      return null;
    }

    case BoundaryTerritoryType.Manual:
    default: {
      return null;
    }
  }
};

export const useGetBoundaryFillEntities = () => {
  const [t] = useTranslation();
  const { getBoundaryGroupById } = useGetBoundaryGroup();
  const spreadSheetColumns = useSpreadsheetColumns();

  return useCallback((boundaryTerritoryGroup?: BoundaryTerritoryGroup | null) => {
    const {
      settings: {
        bucketColumnId: columnId,
        calculateBucketFunction,
        boundaryTerritoryType = BoundaryTerritoryType.Manual,
      },
    } = boundaryTerritoryGroup ?? { settings: {} };

    const columnName = spreadSheetColumns.find(column => column.id === columnId)?.name ??
      t('Unknown column');

    const bucketFunctionOptionName = getCalculateBucketFunctionOptions(t)
      .find(option => option.value === calculateBucketFunction)?.name;

    return getBoundaryFillEntities({
      boundaryTerritoryGroup,
      boundaryTerritoryType,
      bucketFunctionOptionName,
      columnName,
      getBoundaryGroupById,
      t,
    });
  }, [getBoundaryGroupById, spreadSheetColumns, t]);
};

export const concatEntities = (entities: ActiveBoundaryFillInfoEntitiesBase): string => {
  if (!entities) {
    return '';
  }
  if (!entities.subEntity) {
    return entities.mainEntity;
  }
  return `${entities.mainEntity} > ${entities.subEntity}`;
};

export const getNoFillEntities = memoizeOne((): ActiveBoundaryFillInfoEntities => ({
  mainEntity: translate('BoundaryFill.Manual'),
  shortenedMainEntity: translate('BoundaryFill.Manual'),
}));
